// Feature 10 — Demo Mode Lite: IMMUTABLE sample fixtures. No MongoDB, no storage, no AI.
// Every entity id is prefixed "demo_" and carries is_demo:true. These ids can never resolve
// to a real user record (no real record uses the demo_ prefix). Images are BUNDLED local static
// assets (served from frontend/public/demo/) displayed with a visible SAMPLE watermark — never
// user uploads, production storage, or runtime Unsplash CDN calls.

export const SAMPLE_IMAGES = {
  kitchen_in: "/demo/kitchen-movein.jpg",
  kitchen_out: "/demo/kitchen-moveout.jpg",
  bathroom_in: "/demo/bathroom-movein.jpg",
  bathroom_out: "/demo/bathroom-moveout.jpg",
  living_in: "/demo/living-movein.jpg",
  living_out: "/demo/living-moveout.jpg",
  // Bedroom pair uses BUNDLED local static assets (same room; move-out is the same photo
  // darkened locally) so the comparison-limited case is limited by LIGHTING, not a different room.
  bedroom_in: "/demo/bedroom-movein.jpg",
  bedroom_out_dark: "/demo/bedroom-moveout.jpg",
};
export const DEMO_PROPERTIES = {
  move_in: {
    id: "demo_prop_movein", is_demo: true, doc_type: "move_in",
    name: "Sample Apartment — Move-In", address: "100 Sample Street, Unit 2",
    is_locked: true, sealed_at: "2025-01-04T10:12:00Z", fictional: true,
  },
  move_out: {
    id: "demo_prop_moveout", is_demo: true, doc_type: "move_out",
    name: "Sample Apartment — Move-Out", address: "100 Sample Street, Unit 2",
    linked_property_id: "demo_prop_movein", is_locked: false, fictional: true,
  },
};

// ---- Section 1: Evidence Coverage Map (precomputed, internally consistent) ----
// Totals below match the room/slot states exactly (captured 8 of 12 applicable = 67%).
export const DEMO_COVERAGE = {
  is_demo: true, phase: "move_out",
  summary: { captured_views: 8, applicable_views: 12, percentage: 67, denominator_zero: false,
             rooms_complete: 1, rooms_needing_attention: 2, missing_pairs: 1 },
  rooms: [
    { room_id: "demo_room_kitchen", room_name: "Kitchen", status: "complete",
      captured_slots: 3, applicable_slots: 3,
      slots: [
        { slot: "wide_angle", label: "Wide-angle shot", state: "captured", flags: [] },
        { slot: "floor", label: "Floor", state: "captured", flags: [] },
        { slot: "fixtures", label: "Fixtures & appliances", state: "captured", flags: [] },
      ] },
    { room_id: "demo_room_living", room_name: "Living room", status: "needs_attention",
      captured_slots: 2, applicable_slots: 3,
      slots: [
        { slot: "wide_angle", label: "Wide-angle shot", state: "captured", flags: ["unsealed"] },
        { slot: "floor", label: "Floor", state: "captured", flags: [] },
        { slot: "walls", label: "Walls / paint", state: "missing", flags: [] },
      ] },
    { room_id: "demo_room_bedroom", room_name: "Bedroom", status: "needs_attention",
      captured_slots: 2, applicable_slots: 3,
      slots: [
        { slot: "wide_angle", label: "Wide-angle shot", state: "captured", flags: ["too_dark", "label_confirmation_needed"] },
        { slot: "floor", label: "Floor", state: "captured", flags: [] },
        { slot: "closet", label: "Closet", state: "missing", flags: ["missing_pair"] },
      ] },
    { room_id: "demo_room_bathroom", room_name: "Bathroom", status: "needs_attention",
      captured_slots: 1, applicable_slots: 3,
      slots: [
        { slot: "wide_angle", label: "Wide-angle shot", state: "captured", flags: [] },
        { slot: "fixtures", label: "Fixtures", state: "missing", flags: [] },
        { slot: "floor", label: "Floor", state: "missing", flags: [] },
      ] },
  ],
  actions: [
    "Capture the missing Living room walls / paint view.",
    "Retake the Bedroom wide-angle photo — it looks too dark to compare.",
    "Pair the Bedroom closet move-out photo with its sealed baseline.",
    "Seal the Living room wide-angle photo to complete its provenance.",
  ],
};

// ---- Section 2: Proof Replay pairs (2 confirmed: 1 well-aligned, 1 comparison-limited) ----
export const DEMO_PROOF_PAIRS = [
  { pair_id: "demo_pair_living", is_demo: true, room_name: "Living room",
    move_in_image: SAMPLE_IMAGES.living_in, move_out_image: SAMPLE_IMAGES.living_out,
    move_in_date: "2025-01-04", move_out_date: "2025-12-18",
    comparable: true, move_out_sealed: true,
    note: "Well-aligned pair — same wide-angle framing at move-in and move-out." },
  { pair_id: "demo_pair_bedroom", is_demo: true, room_name: "Bedroom",
    move_in_image: SAMPLE_IMAGES.bedroom_in, move_out_image: SAMPLE_IMAGES.bedroom_out_dark,
    move_in_date: "2025-01-04", move_out_date: "2025-12-18",
    comparable: false, move_out_sealed: false, quality_warning: "too_dark",
    note: "Comparison limited — the move-out photo is much darker, so the area cannot be compared reliably." },
];

// ---- Section 3: Pre-Move-Out Self-Audit (static sample results; NO AI) ----
// Neutral, observational wording only. Local-only interactions reset on refresh/exit.
export const DEMO_SELF_AUDIT = {
  is_demo: true,
  counts: { confirmed_pairs: 2, reviewed: 1, awaiting_review: 1, retakes_recommended: 0 },
  pairs: [
    { pair_id: "demo_pair_living", room_name: "Living room",
      move_in_image: SAMPLE_IMAGES.living_in, move_out_image: SAMPLE_IMAGES.living_out,
      move_out_sealed: true, quality_warnings: [],
      ai_draft: {
        comparable: true, limited: false,
        observations: [
          { region: "lower-left wall", description: "A small mark is visible near the lower-left wall in the move-out photo and is not clearly visible in the move-in photo.", status: "open" },
          { region: "floor near window", description: "The floor near the window appears slightly different in tone, but the lighting also differs.", status: "dismissed" },
        ],
      },
      confirmed_observations: [
        { obs_id: "demo_obs_1", source: "user", text: "There is a small scuff near the baseboard by the window in the move-out photo." },
      ],
      outcome: "confirmed_difference",
    },
    { pair_id: "demo_pair_bedroom", room_name: "Bedroom",
      move_in_image: SAMPLE_IMAGES.bedroom_in, move_out_image: SAMPLE_IMAGES.bedroom_out_dark,
      move_out_sealed: false, quality_warnings: ["too_dark"],
      ai_draft: { comparable: false, limited: true,
        reason: "Photos may not be comparable — the move-out photo is much darker. Retake recommended.",
        observations: [] },
      confirmed_observations: [], outcome: "unable_to_compare",
    },
  ],
};

// ---- Section 4: Charge Storyboard (fictional charges; text preserved, never judged) ----
export const DEMO_STORYBOARD = {
  is_demo: true,
  charges: [
    { charge_id: "demo_charge_1", title: "Bedroom wall repainting", amount: 180,
      exhibit_ref: "Exhibit B", photo_refs: [SAMPLE_IMAGES.bedroom_in, SAMPLE_IMAGES.bedroom_out_dark],
      tenant_note: "Move-in photo shows the bedroom wall in the same painted condition; the move-out photo is darker and hard to compare." },
    { charge_id: "demo_charge_2", title: "Kitchen cleaning", amount: 95,
      exhibit_ref: "Exhibit A", photo_refs: [SAMPLE_IMAGES.kitchen_in, SAMPLE_IMAGES.kitchen_out],
      tenant_note: "Kitchen surfaces appear similar in the move-in and move-out photos." },
    { charge_id: "demo_charge_3", title: "Bathroom fixture replacement", amount: 120,
      exhibit_ref: "Exhibit C", photo_refs: [SAMPLE_IMAGES.bathroom_in],
      tenant_note: "The move-in photo documents the bathroom fixtures at the start of the tenancy." },
  ],
};

// ---- Section 5: Exhibit Dossier (read-only sample outline; NO export pipeline) ----
export const DEMO_DOSSIER = {
  is_demo: true,
  exhibits: [
    { no: "A", title: "Kitchen — wide angle, floor, fixtures", provenance: "Sealed move-in baseline · sample", images: [SAMPLE_IMAGES.kitchen_in] },
    { no: "B", title: "Bedroom — wide angle & floor", provenance: "Move-in sealed; move-out unsealed · sample", images: [SAMPLE_IMAGES.bedroom_in] },
    { no: "C", title: "Bathroom — wide angle", provenance: "Sealed move-in baseline · sample", images: [SAMPLE_IMAGES.bathroom_in] },
    { no: "D", title: "Living room — Proof Replay pair", provenance: "Confirmed pair · sample", images: [SAMPLE_IMAGES.living_in, SAMPLE_IMAGES.living_out] },
  ],
  storyboard_placement: "Charge Storyboard appears after the exhibits, cross-referencing Exhibits A–C.",
};

export const DEMO_SECTIONS = [
  { key: "coverage", label: "Coverage" },
  { key: "proof-replay", label: "Proof Replay" },
  { key: "self-audit", label: "Self-Audit" },
  { key: "storyboard", label: "Charge Storyboard" },
  { key: "dossier", label: "Dossier" },
  { key: "condition-history", label: "Condition History" },
  { key: "timeline", label: "Tenancy Timeline" },
  { key: "inspection", label: "Inspection Request" },
];

// ---- Section 6: Condition History (precomputed fixture; NO backend calls, NO logic) ----
// One room (Living room) followed across move-in -> during tenancy -> move-out, with honest
// date-source labels, a sealed and not-yet-sealed example, and a Proof Replay pair indicator.
export const DEMO_CONDITION_HISTORY = {
  is_demo: true,
  address: "100 Sample Street, Unit 2",
  linked_count: 1,
  excluded_unconfirmed_count: 1,
  has_history: true,
  groups: [
    {
      group_key: "demo_history_living",
      label: "Living room",
      correspondence_source: "proof_replay_pair",
      matched_across_stages: true,
      has_proof_replay_pair: true,
      room_refs: [{ stage: "move_in" }, { stage: "move_out" }],
      moments: [
        {
          evidence_id: "demo_ch_1", evidence_type: "photo", stage: "move_in",
          source_type: "tenantseal_capture", date_source: "client_captured_at",
          display_date: "2025-01-04T10:12:00Z", sealed: true,
          thumbnailUrl: SAMPLE_IMAGES.living_in, room_name: "Living room",
          note: "Wide-angle move-in baseline of the living room.", in_proof_replay_pair: true,
        },
        {
          evidence_id: "demo_ch_2", evidence_type: "photo", stage: "during_tenancy",
          source_type: "photo_metadata", date_source: "exif_timestamp", photo_category: "monthly_checkup",
          display_date: "2025-06-15T14:00:00Z", sealed: false,
          thumbnailUrl: SAMPLE_IMAGES.living_in, room_name: "Living room",
          note: "Mid-tenancy checkup photo (date read from the photo's file metadata).", in_proof_replay_pair: false,
        },
        {
          evidence_id: "demo_ch_3", evidence_type: "photo", stage: "move_out",
          source_type: "tenantseal_capture", date_source: "client_captured_at",
          display_date: "2025-12-18T09:30:00Z", sealed: false,
          thumbnailUrl: SAMPLE_IMAGES.living_out, room_name: "Living room",
          note: "Wide-angle move-out photo of the same living room.", in_proof_replay_pair: true,
        },
      ],
    },
  ],
};


// ---- Section 7: Tenancy Timeline (precomputed fixture; NO backend calls, NO logic) ----
// A chronological property-level stream (oldest -> newest) mirroring the free /tenancy-timeline
// endpoint's shape. Sensitive events (communication, rent, forwarding notice) are EXISTENCE-ONLY:
// neutral label, no thumbnail, no contents/amounts/names/addresses. Thumbnails use BUNDLED local
// static assets only (never Unsplash / production storage).
export const DEMO_TIMELINE = {
  is_demo: true,
  address: "100 Sample Street, Unit 2",
  linked_property_ids: ["demo_prop_move_out"],
  events: [
    {
      event_id: "demo_tl_1", event_type: "condition_photos", stage: "move_in",
      label: "Living room — 3 move-in photos captured",
      display_date: "2025-01-04T10:12:00Z", date_source: "client_captured_at",
      date_source_label: "Captured by TenantSeal", count: 3, sealed: true,
      thumbnailUrl: SAMPLE_IMAGES.living_in, existing_route: "#",
    },
    {
      event_id: "demo_tl_2", event_type: "property_sealed", stage: "move_in",
      label: "Documentation sealed",
      display_date: "2025-01-04T11:00:00Z", date_source: "record_created_at",
      date_source_label: "Sealed", count: null, sealed: true, thumbnailUrl: null, existing_route: "#",
    },
    {
      event_id: "demo_tl_3", event_type: "monthly_checkup", stage: "during_tenancy",
      label: "Monthly Checkup recorded",
      display_date: "2025-06-15T14:00:00Z", date_source: "record_created_at",
      date_source_label: "Recorded", count: 2, sealed: null,
      thumbnailUrl: SAMPLE_IMAGES.living_in, existing_route: "#",
    },
    {
      event_id: "demo_tl_4", event_type: "communication", stage: "during_tenancy",
      label: "Communication logged",
      display_date: "2025-07-02T09:30:00Z", date_source: "record_created_at",
      date_source_label: "Logged", count: null, sealed: null, thumbnailUrl: null, existing_route: "#",
    },
    {
      event_id: "demo_tl_5", event_type: "rent", stage: "during_tenancy",
      label: "Rent record added",
      display_date: "2025-08-01T08:00:00Z", date_source: "record_created_at",
      date_source_label: "Recorded", count: null, sealed: null, thumbnailUrl: null, existing_route: "#",
    },
    {
      event_id: "demo_tl_6", event_type: "maintenance", stage: "during_tenancy",
      label: "Maintenance item recorded",
      display_date: "2025-09-20T16:45:00Z", date_source: "record_created_at",
      date_source_label: "Recorded", count: null, sealed: null, thumbnailUrl: null, existing_route: "#",
    },
    {
      event_id: "demo_tl_7", event_type: "condition_photos", stage: "move_out",
      label: "Living room — 3 move-out photos captured",
      display_date: "2025-12-18T09:30:00Z", date_source: "client_captured_at",
      date_source_label: "Captured by TenantSeal", count: 3, sealed: false,
      thumbnailUrl: SAMPLE_IMAGES.living_out, existing_route: "#",
    },
    {
      event_id: "demo_tl_8", event_type: "forwarding_notice", stage: "move_out",
      label: "Forwarding notice recorded",
      display_date: "2025-12-19T10:00:00Z", date_source: "record_created_at",
      date_source_label: "Recorded", count: null, sealed: null, thumbnailUrl: null, existing_route: "#",
    },
    {
      event_id: "demo_tl_9", event_type: "key_return_capsule", stage: "move_out",
      label: "Key Return Capsule sealed — v1",
      display_date: "2025-12-19T17:00:00Z", date_source: "record_created_at",
      date_source_label: "Sealed", count: null, sealed: true, thumbnailUrl: null, existing_route: "#",
    },
  ],
   event_count: 9,
};


// ---- Section 8: Pre-Move-Out Inspection Request (precomputed fixture; NO backend, NO Resend) ----
// Clearly fictional recipient; two preferred windows; a sent/delivered SAMPLE status. Nothing sends.
export const DEMO_INSPECTION = {
  is_demo: true,
  recipient_name: "Property Manager",
  recipient_email: "manager@example.test",
  property_address: "100 Sample Street, Unit 2",
  move_out_date: "2025-12-20",
  windows: [
    { date: "2025-12-15", start: "09:00", end: "10:00" },
    { date: "2025-12-16", start: "14:00", end: "15:30" },
  ],
  optional_message: "Happy to work around your schedule — thank you!",
  subject: "Request to schedule a pre-move-out inspection for 100 Sample Street, Unit 2",
  status: "sent",
  delivery_status: "delivered",
};

